import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import Input from '../../../components/ui/input';
import Label from '../../../components/ui/label';
import { FiAlertCircle, FiCheck, FiFile, FiXCircle, FiArrowLeft, FiUploadCloud, FiChevronDown, FiImage } from 'react-icons/fi';
import ImageLightbox from '../../../components/ui/ImageLightbox';
import ImageOptionCard from '../../../components/forms/ImageOptionCard';
import { Textarea } from '../../../components/ui/textarea';
import { Checkbox } from '../../../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { format } from 'date-fns';
import MapboxPlacesAutocomplete from '../../../components/ui/mapbox-places-autocomplete';
import SublyApi from '../../../helpers/Api';
import { useFormRoute } from '../../../hooks/useRouteParams';
import { parseFieldOptions } from '../../../utils/formHelpers';

// Image rendering is now data-driven via field.appearance.enableImages and option.imageUrl

// Helper function to detect file type from URL
const getFileTypeFromUrl = (url) => {
	if (!url || typeof url !== 'string')
		return { isImage: false, isPdf: false, fileType: 'unknown' };

	const lowerUrl = url.toLowerCase();

	// Check for image extensions
	const imageExtensions = [
		'.jpg',
		'.jpeg',
		'.png',
		'.gif',
		'.webp',
		'.bmp',
		'.svg',
	];
	const isImage = imageExtensions.some((ext) => lowerUrl.includes(ext));

	// Check for PDF
	const isPdf = lowerUrl.includes('.pdf');

	// Determine file type
	let fileType = 'file';
	if (isImage) fileType = 'image';
	else if (isPdf) fileType = 'pdf';
	else if (lowerUrl.includes('.doc') || lowerUrl.includes('.docx'))
		fileType = 'document';
	else if (lowerUrl.includes('.xls') || lowerUrl.includes('.xlsx'))
		fileType = 'spreadsheet';
	else if (lowerUrl.includes('.ppt') || lowerUrl.includes('.pptx'))
		fileType = 'presentation';

	return { isImage, isPdf, fileType };
};

// Elegant File Upload Component with drag-and-drop
const FileUploadField = ({
	field,
	fieldValue,
	fieldError,
	handleInputChange,
	setErrors,
	token,
	form,
	submitting,
	fieldWrapper
}) => {
	const [isDragOver, setIsDragOver] = useState(false);

	const renderCurrentFilePreview = () => {
		if (!fieldValue || fieldValue === 'Uploading...') return null;

		const { isImage, isPdf, fileType } = getFileTypeFromUrl(fieldValue);

		return (
			<div className='flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 shadow-sm'>
				<div className='flex items-center space-x-4'>
					{isImage ? (
						<div className='relative w-16 h-16 rounded-lg overflow-hidden border border-gray-200 shadow-sm'>
							<img
								src={fieldValue}
								alt='Current file'
								className='w-full h-full object-cover'
								onError={(e) => {
									e.target.style.display = 'none';
									e.target.nextSibling.style.display = 'flex';
								}}
							/>
							<div
								style={{ display: 'none' }}
								className='absolute inset-0 flex items-center justify-center bg-gray-100'>
								<FiImage className='h-6 w-6 text-gray-400' />
							</div>
						</div>
					) : isPdf ? (
						<div className='w-16 h-16 rounded-lg bg-red-50 border border-red-100 flex items-center justify-center'>
							<FiFile className='h-8 w-8 text-red-500' />
						</div>
					) : (
						<div className='w-16 h-16 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center'>
							<FiFile className='h-8 w-8 text-gray-500' />
						</div>
					)}
					<div>
						<div className='text-sm font-montserrat-medium text-gray-900 mb-1'>
							{isPdf ? 'PDF Document' : isImage ? 'Image File' : 'File'}
						</div>
						<a
							href={fieldValue}
							target='_blank'
							rel='noopener noreferrer'
							className='text-xs font-montserrat-medium text-gray-600 hover:text-gray-900 underline underline-offset-2 transition-colors'>
							{isPdf
								? 'View PDF'
								: isImage
								? 'View full size'
								: 'Download file'}
						</a>
					</div>
				</div>
				<button
					type='button'
					onClick={async () => {
						const fileUrl = fieldValue;

						// Clear field value immediately for better UX
						handleInputChange(field, '');

						// Reset file input to ensure onChange fires on reselection of same file
						const fileInput = document.getElementById(`file-${field.field_key}`);
						if (fileInput) {
							fileInput.value = '';
						}

						// Delete file from server (works for both temp and permanent files)
						if (fileUrl && typeof fileUrl === 'string' && fileUrl.includes('blob.vercel-storage.com')) {
							try {
								await SublyApi.request(
									'api/forms/delete-file',
									{ fileUrl },
									'delete',
									{
										Authorization: `Bearer ${token}`,
										'Content-Type': 'application/json',
									}
								);
							} catch (error) {
								console.error('Failed to delete file:', error);
								// Don't show error to user - temp files will be cleaned up automatically in 24h
							}
						}
					}}
					className='p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all duration-200'
					title='Remove file'>
					<FiXCircle className='h-5 w-5' />
				</button>
			</div>
		);
	};

	const handleFileUpload = async (file) => {
		if (!file) return;

		// Client-side file type validation
		if (field.validation_rules?.fileTypes?.length > 0) {
			const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
			const allowedTypes = field.validation_rules.fileTypes.map(type => type.toLowerCase());

			if (!allowedTypes.includes(fileExtension)) {
				setErrors((prev) => ({
					...prev,
					[field.field_key]: `File type "${fileExtension}" is not allowed. Allowed types: ${field.validation_rules.fileTypes.join(', ')}`
				}));
				return;
			}
		}

		// Client-side file size validation
		const maxSize = 30 * 1024 * 1024; // 30MB
		if (file.size > maxSize) {
			setErrors((prev) => ({
				...prev,
				[field.field_key]: `File "${file.name}" is too large. Maximum size is 30MB.`
			}));
			return;
		}

		// Show loading state
		handleInputChange(field, 'Uploading...');

		try {
			// Create FormData for direct upload
			const uploadFormData = new FormData();
			uploadFormData.append('file', file);
			uploadFormData.append('fieldId', field.id);
			uploadFormData.append('formId', form.id);

			// Upload directly to user endpoint
			const response = await SublyApi.request(
				'api/forms/upload-file',
				uploadFormData,
				'post',
				{
					Authorization: `Bearer ${token}`,
					'Content-Type': 'multipart/form-data',
				}
			);

			// Update field with file URL
			handleInputChange(field, response.url);
			// Clear any previous errors for this field
			setErrors((prev) => ({
				...prev,
				[field.field_key]: ''
			}));
		} catch (error) {
			console.error('Upload error details:', {
				error,
				message: error.message,
				response: error.response?.data,
				status: error.response?.status
			});
			handleInputChange(field, '');

			// Extract the actual error message from server response
			let errorMessage = 'Failed to upload file';

			// The API.js throws errors in different formats
			if (Array.isArray(error)) {
				// When API.js throws an array of messages
				errorMessage = error[0] || 'Failed to upload file';
			} else if (typeof error === 'string') {
				// When API.js throws a single message string
				errorMessage = error;
			} else if (error.response?.data?.message) {
				errorMessage = error.response.data.message;
			} else if (error.response?.data?.error) {
				errorMessage = error.response.data.error;
			} else if (error.message) {
				errorMessage = error.message;
			}

			setErrors((prev) => ({
				...prev,
				[field.field_key]: errorMessage
			}));
		}
	};

	return fieldWrapper(
		<div className='space-y-4'>
			{/* Current file preview */}
			{fieldValue &&
				fieldValue !== 'Uploading...' &&
				renderCurrentFilePreview()}

			{/* Upload status */}
			{fieldValue === 'Uploading...' && (
				<div className='flex items-center justify-center p-6 bg-gray-50 rounded-lg border border-gray-200'>
					<div className='flex flex-col items-center'>
						<div className='relative w-12 h-12 mb-3'>
							<div className='absolute inset-0 border-3 border-gray-200 rounded-full'></div>
							<div className='absolute inset-0 border-3 border-t-gray-900 rounded-full animate-spin'></div>
						</div>
						<span className='text-sm font-montserrat-medium text-gray-700'>Uploading your file...</span>
						<span className='text-xs font-montserrat-regular text-gray-500 mt-1'>Please wait</span>
					</div>
				</div>
			)}

			{/* Elegant drag-drop upload interface */}
			{!fieldValue && (
				<label
					htmlFor={`file-${field.field_key}`}
					className={`relative block cursor-pointer transition-all duration-300 ease-in-out
						${isDragOver
							? 'transform scale-[1.01]'
							: ''
						}
					`}
					onDragOver={(e) => {
						e.preventDefault();
						setIsDragOver(true);
					}}
					onDragLeave={(e) => {
						e.preventDefault();
						setIsDragOver(false);
					}}
					onDrop={(e) => {
						e.preventDefault();
						setIsDragOver(false);
						const file = e.dataTransfer.files?.[0];
						if (file) {
							handleFileUpload(file);
						}
					}}
				>
					<div className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300
						${isDragOver
							? 'border-gray-900 bg-gray-50 shadow-lg'
							: 'border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50/50'
						}
					`}>
						<div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-all duration-300
							${isDragOver
								? 'bg-gray-900 text-white scale-110'
								: 'bg-gray-100 text-gray-400'
							}
						`}>
							<FiUploadCloud className='h-8 w-8' />
						</div>
						<div className='space-y-2'>
							<p className='text-base font-montserrat-medium text-gray-900'>
								{isDragOver ? 'Drop your file here' : 'Drag and drop your file here'}
							</p>
							<p className='text-sm font-montserrat-regular text-gray-500'>
								or <span className='text-gray-900 underline underline-offset-2'>browse</span> to choose a file
							</p>
						</div>
						<div className='mt-4 pt-4 border-t border-gray-100'>
							<p className='text-xs font-montserrat-regular text-gray-400'>
								{field.validation_rules?.fileTypes?.length > 0
									? `Accepted: ${field.validation_rules.fileTypes.join(', ')}`
									: (field.type === 'image' ? 'Accepted: Images (JPG, PNG, GIF, WebP)' : 'All file types accepted')
								}
								{' '}&bull;{' '}Max size: 30MB
							</p>
						</div>
					</div>
					<input
						id={`file-${field.field_key}`}
						type='file'
						className='sr-only'
						onChange={(e) => {
							const file = e.target.files?.[0];
							if (file) {
								handleFileUpload(file);
							}
						}}
						accept={
							field.validation_rules?.fileTypes?.length > 0
								? field.validation_rules.fileTypes.join(',')
								: (field.type === 'image' ? 'image/*' : '*')
						}
						disabled={submitting}
					/>
				</label>
			)}

			{/* Replace file option when file exists */}
			{fieldValue && fieldValue !== 'Uploading...' && (
				<label
					htmlFor={`file-${field.field_key}`}
					className='block cursor-pointer'
				>
					<div className='border border-gray-200 rounded-lg p-3 text-center hover:bg-gray-50 transition-colors duration-200'>
						<p className='text-sm font-montserrat-medium text-gray-600'>
							<FiUploadCloud className='inline-block h-4 w-4 mr-2 -mt-0.5' />
							Replace with a different file
						</p>
					</div>
					<input
						id={`file-${field.field_key}`}
						type='file'
						className='sr-only'
						onChange={(e) => {
							const file = e.target.files?.[0];
							if (file) {
								handleFileUpload(file);
							}
						}}
						accept={
							field.validation_rules?.fileTypes?.length > 0
								? field.validation_rules.fileTypes.join(',')
								: (field.type === 'image' ? 'image/*' : '*')
						}
						disabled={submitting}
					/>
				</label>
			)}
		</div>
	);
};

// Custom styled Select component for FormRenderer with luxury styling
const StyledSelect = ({
	id,
	name,
	value,
	onChange,
	options,
	className,
	error,
	...props
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const selectRef = useRef(null);

	// Find selected option label
	const selectedOption = options.find(opt => (opt.value || opt) === value);
	const selectedLabel = selectedOption ? (selectedOption.label || selectedOption) : '';

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (selectRef.current && !selectRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	return (
		<div ref={selectRef} className="relative">
			<button
				type="button"
				id={id}
				onClick={() => setIsOpen(!isOpen)}
				className={`w-full px-4 py-3 text-left bg-white border ${
					error ? 'border-red-400' : isOpen ? 'border-[#CC1DB9]' : 'border-gray-200'
				} rounded-lg shadow-sm font-montserrat-regular text-gray-900
				focus:outline-none focus:ring-2 focus:ring-[#CC1DB9]/40 focus:border-[#CC1DB9] focus:shadow-md
				transition-all duration-200 ease-in-out
				hover:border-gray-400 flex items-center justify-between`}
				{...props}
			>
				<span className={value ? 'text-gray-900' : 'text-gray-400'}>
					{selectedLabel || 'Select an option'}
				</span>
				<FiChevronDown className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
			</button>

			{isOpen && (
				<div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
					<div className="max-h-60 overflow-y-auto">
						{options.map((option, index) => {
							const optionValue = option.value || option;
							const optionLabel = option.label || option;
							const isSelected = optionValue === value;

							return (
								<button
									key={index}
									type="button"
									onClick={() => {
										onChange(optionValue);
										setIsOpen(false);
									}}
									className={`w-full px-4 py-3 text-left font-montserrat-regular transition-colors duration-150
										${isSelected
											? 'bg-gray-900 text-white'
											: 'text-gray-700 hover:bg-gray-50'
										}
										${index !== options.length - 1 ? 'border-b border-gray-100' : ''}
									`}
								>
									<div className="flex items-center justify-between">
										<span>{optionLabel}</span>
										{isSelected && <FiCheck className="h-4 w-4" />}
									</div>
								</button>
							);
						})}
					</div>
				</div>
			)}

			{/* Hidden native select for form submission */}
			<select
				name={name}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				className="sr-only"
				tabIndex={-1}
			>
				<option value=''>Select an option</option>
				{options.map((option, index) => (
					<option key={index} value={option.value || option}>
						{option.label || option}
					</option>
				))}
			</select>
		</div>
	);
};

const FormRenderer = ({ formId: propFormId, onSubmit, onCancel, editingSubmission = null, isEditing = false, backButtonText = 'Back to Forms' }) => {
	const currentUser = useSelector((st) => st.user.currentUser);
	const token = useSelector((st) => st.user.token);
	
	// Get form ID from props or URL parameters
	const { formId: routeFormId, navigate } = useFormRoute();
	const formId = propFormId || routeFormId;

	// State - must be declared before any conditional returns
	const [form, setForm] = useState(null);
	const [formData, setFormData] = useState({});
	const [errors, setErrors] = useState({});
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState('');
	const [validationModal, setValidationModal] = useState({
		show: false,
		message: '',
	});
	const [lightboxImage, setLightboxImage] = useState(null);

	// Draft functionality
	const [isDraftSaving, setIsDraftSaving] = useState(false);
	const [lastSaved, setLastSaved] = useState(null);
	const [draftKey, setDraftKey] = useState(null);

	// Progress tracking
	const [sectionProgress, setSectionProgress] = useState({});

	
	// Enhanced cancel handler that supports URL navigation
	const handleCancel = () => {
		if (onCancel) {
			onCancel();
		} else {
			// If no onCancel prop provided, navigate back to forms dashboard
			navigate.toDashboard();
		}
	};

	// Fetch form data
	useEffect(() => {
		const fetchForm = async () => {
			try {
				setLoading(true);
				const response = await SublyApi.request(
					`api/forms/${formId}`,
					{},
					'get',
					{
						Authorization: `Bearer ${token}`,
					}
				);

				setForm(response.form);


				// Initialize form data with empty values
				const initialData = {};
				const allFields = [
					...(response.form?.fields || []),
					...(response.form?.sections?.flatMap(
						(section) => section.fields || []
					) || []),
				];

				allFields.forEach((field) => {
					// Parse appearance if it's a string
					let appearance = field.appearance;
					if (typeof appearance === 'string' && appearance) {
						try {
							appearance = JSON.parse(appearance);
						} catch (e) {
							console.error('Error parsing appearance for default values:', e);
							appearance = {};
						}
					} else {
						appearance = appearance || {};
					}

					const fieldType = field.type?.toLowerCase();

					// Skip display-only fields that don't need form data
					if (['heading', 'paragraph', 'divider'].includes(fieldType)) {
						return;
					}

					if (appearance?.defaultValue) {
						initialData[field.field_key] = appearance.defaultValue;
					} else {
						switch (fieldType) {
							case 'checkbox':
							case 'multiselect':
								initialData[field.field_key] = [];
								break;
							case 'number':
							case 'price':
								initialData[field.field_key] = '';
								break;
							case 'address':
								initialData[field.field_key] = {
									street: '',
									city: '',
									state: '',
									zip: '',
								};
								break;
							default:
								initialData[field.field_key] = '';
						}
					}
				});

				// Handle different data loading scenarios
				if (isEditing && editingSubmission) {
					if (editingSubmission.isDraft) {
						// Load draft data from localStorage
						const draftKey = `form_draft_${formId}_${currentUser?.username}`;
						const savedDraft = localStorage.getItem(draftKey);
						if (savedDraft) {
							try {
								const draftData = JSON.parse(savedDraft);
								const mergedData = { ...initialData, ...draftData.formData };
								setFormData(mergedData);
								setLastSaved(new Date(draftData.timestamp));
							} catch (e) {
								console.error('Error loading draft for editing:', e);
								setFormData(initialData);
							}
						} else {
							setFormData(initialData);
						}
						setDraftKey(draftKey);
					} else {
						// Load existing submission data for editing
						const existingData = typeof editingSubmission.data === 'string' 
							? JSON.parse(editingSubmission.data) 
							: editingSubmission.data;
						const mergedData = { ...initialData, ...existingData };
						setFormData(mergedData);
					}
				} else {
					// Generate draft key for this form/user combination (only for new submissions)
					const generatedDraftKey = `form_draft_${formId}_${currentUser?.username}`;
					setDraftKey(generatedDraftKey);

					// Try to load existing draft
					const savedDraft = localStorage.getItem(generatedDraftKey);
					if (savedDraft) {
						try {
							const draftData = JSON.parse(savedDraft);
							// Merge draft data with initial data, preserving draft values
							const mergedData = { ...initialData, ...draftData.formData };
							setFormData(mergedData);
							setLastSaved(new Date(draftData.timestamp));
						} catch (e) {
							console.error('Error loading draft:', e);
							setFormData(initialData);
						}
					} else {
						setFormData(initialData);
					}
				}
			} catch (err) {
				console.error('Error fetching form:', err);
				setError('Failed to load form');
			} finally {
				setLoading(false);
			}
		};

		if (formId && token) {
			fetchForm();
		}
	}, [formId, token, currentUser]);

	// Auto-save draft functionality
	const saveDraft = async (data) => {
		if (!draftKey || !data) return;

		try {
			setIsDraftSaving(true);
			const draftData = {
				formData: data,
				timestamp: new Date().toISOString(),
				formId,
				formName: form?.name
			};
			
			localStorage.setItem(draftKey, JSON.stringify(draftData));
			setLastSaved(new Date());
		} catch (e) {
			console.error('Error saving draft:', e);
		} finally {
			setIsDraftSaving(false);
		}
	};

	// Clear draft when form is submitted successfully
	const clearDraft = () => {
		if (draftKey) {
			localStorage.removeItem(draftKey);
			setLastSaved(null);
		}
	};

	// Auto-save debounced effect
	useEffect(() => {
		if (!form || !draftKey || Object.keys(formData).length === 0) return;

		const timeoutId = setTimeout(() => {
			// Only save if there's meaningful data (not just empty values)
			const hasData = Object.values(formData).some(value => {
				if (Array.isArray(value)) return value.length > 0;
				if (typeof value === 'object' && value !== null) {
					return Object.values(value).some(v => v && v !== '');
				}
				return value && value !== '';
			});

			if (hasData) {
				saveDraft(formData);
			}
		}, 2000); // Auto-save every 2 seconds after user stops typing

		return () => clearTimeout(timeoutId);
	}, [formData, form, draftKey]);

	// Helper: Check if a field has a value
	const isFieldFilled = (field) => {
		const value = formData[field.field_key];
		if (Array.isArray(value)) return value.length > 0;
		if (typeof value === 'object' && value !== null) {
			return Object.values(value).some(v => v && v !== '');
		}
		return value && value !== '';
	};

	// Helper: Filter out display-only fields
	const getInputFields = (fields) => {
		return fields.filter(field => {
			const fieldType = field.type?.toLowerCase();
			return !['heading', 'paragraph', 'divider'].includes(fieldType);
		});
	};

	// Helper: Get required fields from a list
	const getRequiredFields = (fields) => {
		return getInputFields(fields).filter(field => field.is_required === true);
	};

	// Helper: Check if any field in a list is filled
	const hasAnyFilledField = (fields) => {
		return getInputFields(fields).some(field => isFieldFilled(field));
	};

	// Helper: Build progress object with isCompleted
	const buildProgressObject = (percentage, hasRequiredFields, hasFilledFields) => {
		const isCompleted = hasRequiredFields ? percentage === 100 : hasFilledFields;
		return { percentage, hasRequiredFields, hasFilledFields, isCompleted };
	};

	// Calculate section progress (based on required fields only)
	const calculateSectionProgress = (sectionId) => {
		if (!form) return buildProgressObject(0, false, false);

		const sectionFields = getFieldsForSection(sectionId);
		const requiredFields = getRequiredFields(sectionFields);
		const hasFilledFields = hasAnyFilledField(sectionFields);

		// No required fields - return special state
		if (requiredFields.length === 0) {
			return buildProgressObject(0, false, hasFilledFields);
		}

		const completedRequiredFields = requiredFields.filter(field => isFieldFilled(field));
		const percentage = Math.round((completedRequiredFields.length / requiredFields.length) * 100);

		return buildProgressObject(percentage, true, hasFilledFields);
	};

	// Calculate overall form progress (based on required fields only)
	const calculateOverallProgress = () => {
		if (!form) return buildProgressObject(0, false, false);

		const allFields = [
			...(form?.fields || []),
			...(form?.sections?.flatMap((section) => section.fields || []) || []),
		];

		const requiredFields = getRequiredFields(allFields);
		const hasFilledFields = hasAnyFilledField(allFields);

		// No required fields - return special state
		if (requiredFields.length === 0) {
			return buildProgressObject(0, false, hasFilledFields);
		}

		const completedRequiredFields = requiredFields.filter(field => isFieldFilled(field));
		const percentage = Math.round((completedRequiredFields.length / requiredFields.length) * 100);

		return buildProgressObject(percentage, true, hasFilledFields);
	};

	// Calculate progress for unassigned fields (Additional Information section)
	const calculateUnassignedFieldsProgress = () => {
		if (!form?.fields) return buildProgressObject(0, false, false);

		const unassignedFields = form.fields;
		const requiredFields = getRequiredFields(unassignedFields);
		const hasFilledFields = hasAnyFilledField(unassignedFields);

		// No required fields - return special state
		if (requiredFields.length === 0) {
			return buildProgressObject(0, false, hasFilledFields);
		}

		const completedRequiredFields = requiredFields.filter(field => isFieldFilled(field));
		const percentage = Math.round((completedRequiredFields.length / requiredFields.length) * 100);

		return buildProgressObject(percentage, true, hasFilledFields);
	};

	// Helper: Render progress indicator for sections
	const renderProgressIndicator = (progress) => {
		const { percentage, hasRequiredFields, hasFilledFields, isCompleted } = progress;

		if (hasRequiredFields) {
			return (
				<div className='flex items-center gap-2'>
					<span className='text-xs font-montserrat-regular text-gray-400 tabular-nums'>
						{percentage}%
					</span>
					<div className='w-16 bg-gray-200/60 rounded-full h-1'>
						<div
							className='h-1 rounded-full transition-all duration-500 ease-out bg-gradient-to-r from-[#CC1DB9] to-[#CC1DB9]/70'
							style={{ width: `${percentage}%` }}
						></div>
					</div>
				</div>
			);
		} else if (hasFilledFields) {
			return (
				<span className='text-xs font-montserrat-regular text-gray-400 uppercase tracking-wider'>
					Complete
				</span>
			);
		} else {
			return (
				<span className='text-xs font-montserrat-regular text-gray-400'>
					Optional
				</span>
			);
		}
	};

	// Update section progress when form data changes
	useEffect(() => {
		if (!form?.sections) return;
		
		const newProgress = {};
		form.sections.forEach(section => {
			newProgress[section.id] = calculateSectionProgress(section.id);
		});
		setSectionProgress(newProgress);
	}, [formData, form]);

	const validateField = (field, value) => {
		const rules = field.validation_rules;
		const fieldType = field.type?.toLowerCase();

		// Skip validation for display-only fields
		if (['heading', 'paragraph', 'divider'].includes(fieldType)) {
			return '';
		}

		if (!rules) return '';

		if (field.is_required && (!value || value.length === 0)) {
			return field.validation_rules.errorMessage || 'This field is required';
		}

		if (!value) return ''; // Skip other validations if field is empty and not required

		switch (fieldType) {
			case 'text':
			case 'textarea':
				if (rules.minLength && value.length < rules.minLength) {
					return `Minimum length is ${rules.minLength} characters`;
				}
				if (rules.maxLength && value.length > rules.maxLength) {
					return `Maximum length is ${rules.maxLength} characters`;
				}
				if (rules.pattern && !new RegExp(rules.pattern).test(value)) {
					return rules.errorMessage || 'Invalid format';
				}
				break;

			case 'number':
			case 'price':
				const num = Number(value);
				if (isNaN(num)) return 'Please enter a valid number';
				if (rules.numberRange?.min && num < rules.numberRange.min) {
					return `Minimum value is ${rules.numberRange.min}`;
				}
				if (rules.numberRange?.max && num > rules.numberRange.max) {
					return `Maximum value is ${rules.numberRange.max}`;
				}
				break;

			case 'email': {
				// Support comma-separated emails for fields labeled "email(s)"
				const emails = value.split(',').map(e => e.trim()).filter(e => e.length > 0);
				const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
				for (const email of emails) {
					if (!emailRegex.test(email)) {
						return emails.length > 1
							? `Invalid email address: "${email}"`
							: 'Please enter a valid email address';
					}
				}
				break;
			}

			case 'phone':
			case 'tel':
				if (!/^[0-9\s\-\(\)\+\.]+$/.test(value)) {
					return 'Please enter a valid phone number';
				}
				break;

			case 'url':
				try {
					const urlToTest = /^https?:\/\//i.test(value) ? value : `https://${value}`;
					new URL(urlToTest);
				} catch {
					return 'Please enter a valid URL';
				}
				break;

			case 'date':
				if (
					rules.dateRange?.min &&
					new Date(value) < new Date(rules.dateRange.min)
				) {
					return `Date must be after ${format(
						new Date(rules.dateRange.min),
						'PP'
					)}`;
				}
				if (
					rules.dateRange?.max &&
					new Date(value) > new Date(rules.dateRange.max)
				) {
					return `Date must be before ${format(
						new Date(rules.dateRange.max),
						'PP'
					)}`;
				}
				break;
		}

		return '';
	};

	const handleInputChange = (field, value) => {
		setFormData((prev) => ({
			...prev,
			[field.field_key]: value,
		}));

		const error = validateField(field, value);
		setErrors((prev) => ({
			...prev,
			[field.field_key]: error,
		}));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		// Validate all fields
		const newErrors = {};
		const missingRequiredFields = [];
		const invalidFields = [];
		let hasErrors = false;

		const allFields = [
			...(form?.fields || []),
			...(form?.sections?.flatMap((section) => section.fields || []) || []),
		];

		// Check if form is completely empty (no required fields and nothing filled)
		const { hasRequiredFields, hasFilledFields } = calculateOverallProgress();
		if (!hasRequiredFields && !hasFilledFields) {
			setValidationModal({
				show: true,
				message: 'Please fill in at least one field before submitting the form.',
			});
			return;
		}

		allFields.forEach((field) => {
			const fieldType = field.type?.toLowerCase();

			// Skip validation for display-only fields
			if (['heading', 'paragraph', 'divider'].includes(fieldType)) {
				return;
			}

			const fieldValue = formData[field.field_key];
			const error = validateField(field, fieldValue);

			if (error) {
				newErrors[field.field_key] = error;
				hasErrors = true;

				// Categorize errors
				if (field.is_required && (!fieldValue || fieldValue.length === 0)) {
					missingRequiredFields.push(field.label);
				} else {
					invalidFields.push(`${field.label}: ${error}`);
				}
			}
		});

		setErrors(newErrors);

		if (hasErrors) {
			// Only show popup for multiple errors (3+), otherwise let inline validation handle it
			if (missingRequiredFields.length + invalidFields.length >= 3) {
				// Create a comprehensive error message
				let modalMessage = '';

				if (missingRequiredFields.length > 0) {
					modalMessage += `Please fill in the following required fields: ${missingRequiredFields.join(
						', '
					)}`;
				}

				if (invalidFields.length > 0) {
					if (modalMessage) modalMessage += '\n\n';
					modalMessage += `Please fix the following validation errors:\n${invalidFields.join(
						'\n'
					)}`;
				}

				setValidationModal({
					show: true,
					message: modalMessage,
				});
			}
			return;
		}

		// Form is valid, submit based on editing mode and form type
		setSubmitting(true);
		try {
			if (isEditing && !editingSubmission.isDraft) {
				// Editing an existing submission
				await handleSubmissionUpdate();
			} else {
				// New submission (or completing a draft)
				await handleTransactionSubmission();
			}

			// Clear the draft after successful submission (for new submissions or completed drafts)
			if (!isEditing || editingSubmission.isDraft) {
				clearDraft();
			}

			if (onSubmit) {
				onSubmit(formData);
			}
		} catch (err) {
			console.error('Form submission error:', err);
			setError('Failed to submit form. Please try again.');
		} finally {
			setSubmitting(false);
		}
	};

	// Handle transaction form submission (standard submission)
	const handleTransactionSubmission = async () => {
		await SublyApi.request(
			`api/forms/${formId}/submit`,
			{
				data: formData,
			},
			'post',
			{
				Authorization: `Bearer ${token}`,
			}
		);
	};

	// Handle submission update (editing existing submission)
	const handleSubmissionUpdate = async () => {
		await SublyApi.updateFormSubmission(
			editingSubmission.id,
			{
				data: formData,
			},
			token
		);
	};


	// Get fields for a section
	const getFieldsForSection = (sectionId) => {
		if (!form?.sections) return [];
		const section = form.sections.find((s) => s.id === sectionId);
		return section?.fields || [];
	};

	// Get unassigned fields (not in any section)
	const getUnassignedFields = () => {
		if (!form?.fields) return [];
		return form.fields;
	};

	// Render a single field based on its type
	const renderField = (field) => {
		const fieldValue = formData[field.field_key] || '';
		const fieldError = errors[field.field_key];

		// Parse appearance data if it's a string
		let appearance = field.appearance;
		if (typeof appearance === 'string' && appearance) {
			try {
				appearance = JSON.parse(appearance);
			} catch (e) {
				console.error('Error parsing appearance in renderer:', e);
				appearance = {};
			}
		} else {
			appearance = appearance || {};
		}

		// Parse options data if it's a string (for select, multiselect, radio, checkbox)
		const options = parseFieldOptions(field.options);

		// Create a field copy with parsed options for rendering
		const fieldWithParsedOptions = { ...field, options };

		// Enhanced luxury field wrapper with sophisticated typography and spacing
		const fieldWrapper = (children) => (
			<div className='group'>
				<Label
					htmlFor={field.field_key}
					className='block text-xs font-montserrat-medium text-gray-500 mb-3 uppercase tracking-[0.1em] transition-colors duration-200 group-focus-within:text-black'>
					{field.label}
					{field.is_required && <span className='text-[#CC1DB9] ml-1 text-[11px] align-super font-montserrat-medium'>*</span>}
				</Label>
				<div className='mt-2'>{children}</div>
				{field.description && (
					<p className='text-sm font-montserrat-regular text-gray-500 mt-2 leading-relaxed'>
						{field.description}
					</p>
				)}
				{fieldError && (
					<div role='alert' aria-live='polite' className='mt-2 px-0 py-1'>
						<p className='text-xs text-red-500 font-montserrat-regular'>
							{fieldError}
						</p>
					</div>
				)}
			</div>
		);

		// Size class based on appearance
		const sizeClass =
			appearance.size === 'small'
				? 'text-sm py-2'
				: appearance.size === 'large'
				? 'text-base py-4'
				: 'py-3';

		// Full width for form fields
		const widthClass = 'w-full';

		// Enhanced input base classes with luxury styling and smooth transitions
		const inputClasses = `${widthClass} ${sizeClass} px-4 border ${
			fieldError ? 'border-red-400 bg-red-50/30' : 'border-gray-300 bg-white'
		} rounded-lg
		font-montserrat-regular text-gray-900 placeholder-gray-500
		focus:outline-none focus:ring-2 focus:ring-[#CC1DB9]/40 focus:border-[#CC1DB9] focus:bg-white
		hover:border-gray-400
		transition-[border-color,box-shadow,background-color] duration-300 ease-in-out`;

		const fieldType = field.type?.toLowerCase();

		switch (fieldType) {
			case 'text':
				return fieldWrapper(
					<Input
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(e) => handleInputChange(field, e.target.value)}
						placeholder={field.placeholder}
						required={field.is_required}
						className={inputClasses}
					/>
				);

			case 'textarea':
				return fieldWrapper(
					<Textarea
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(e) => handleInputChange(field, e.target.value)}
						placeholder={field.placeholder}
						required={field.is_required}
						className={inputClasses}
					/>
				);

			case 'number':
				return fieldWrapper(
					<Input
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(e) => handleInputChange(field, e.target.value)}
						type='number'
						placeholder={field.placeholder}
						required={field.is_required}
						className={inputClasses}
					/>
				);

			case 'email':
				return fieldWrapper(
					<Input
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(e) => handleInputChange(field, e.target.value)}
						type='text'
						inputMode='email'
						placeholder={field.placeholder}
						required={field.is_required}
						className={inputClasses}
					/>
				);

			case 'phone':
			case 'tel':
				return fieldWrapper(
					<Input
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(e) => handleInputChange(field, e.target.value)}
						type='tel'
						placeholder={field.placeholder}
						required={field.is_required}
						pattern='[0-9\s\-\(\)\+\.]+'
						title='Please enter a valid phone number'
						className={inputClasses}
					/>
				);

			case 'url':
				return fieldWrapper(
					<Input
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(e) => handleInputChange(field, e.target.value)}
						onBlur={(e) => {
							const v = e.target.value.trim();
							if (v && !/^https?:\/\//i.test(v)) {
								handleInputChange(field, `https://${v}`);
							}
						}}
						type='text'
						placeholder={field.placeholder || 'e.g. example.com'}
						required={field.is_required}
						className={inputClasses}
					/>
				);

			case 'date':
				return fieldWrapper(
					<Input
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(e) => handleInputChange(field, e.target.value)}
						type='date'
						placeholder={field.placeholder}
						required={field.is_required}
						className={inputClasses}
					/>
				);

			case 'time':
				return fieldWrapper(
					<Input
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(e) => handleInputChange(field, e.target.value)}
						type='time'
						placeholder={field.placeholder}
						required={field.is_required}
						className={inputClasses}
					/>
				);

			case 'datetime':
				return fieldWrapper(
					<Input
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(e) => handleInputChange(field, e.target.value)}
						type='datetime-local'
						placeholder={field.placeholder}
						required={field.is_required}
						className={inputClasses}
					/>
				);

			case 'select':
				if (options.length > 0) {
					return fieldWrapper(
						<StyledSelect
							id={field.field_key}
							name={field.field_key}
							value={fieldValue}
							onChange={(value) => handleInputChange(field, value)}
							options={options}
							error={fieldError}
						/>
					);
				}
				// Fallback to text input when no options are available
				return fieldWrapper(
					<Input
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(e) => handleInputChange(field, e.target.value)}
						placeholder={field.placeholder || 'Enter value'}
						required={field.is_required}
						className={inputClasses}
					/>
				);

			case 'radio':
				if (options.length > 0) {
					// Check if field has images enabled and options have imageUrls
					const radioHasImages = appearance?.enableImages && options.some(opt => opt.imageUrl);

					if (radioHasImages) {
						// Image card variant
						return fieldWrapper(
							<RadioGroup
								id={field.field_key}
								name={field.field_key}
								value={fieldValue}
								onValueChange={(value) => handleInputChange(field, value)}
								className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'>
								{options.map((option) => {
									const optionValue = option.value || option;
									const isSelected = fieldValue === optionValue;
									return (
										<ImageOptionCard
											key={optionValue}
											option={option}
											fieldKey={field.field_key}
											isSelected={isSelected}
											variant='radio'
											onImageClick={setLightboxImage}
										>
											<RadioGroupItem
												value={optionValue}
												id={`${field.field_key}-${optionValue}`}
												className='sr-only'
											/>
										</ImageOptionCard>
									);
								})}
							</RadioGroup>
						);
					}

					// Text-only card variant (no images)
					return fieldWrapper(
						<RadioGroup
							id={field.field_key}
							name={field.field_key}
							value={fieldValue}
							onValueChange={(value) => handleInputChange(field, value)}
							className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
							{options.map((option) => {
								const optionValue = option.value || option;
								const optionLabel = option.label || option;
								const isSelected = fieldValue === optionValue;
								return (
									<label
										key={optionValue}
										htmlFor={`${field.field_key}-${optionValue}`}
										className={`relative flex items-center p-4 rounded-lg border cursor-pointer transition-all duration-300 ease-in-out
											${isSelected
												? 'border-black bg-gray-50/50'
												: 'border-gray-200 bg-white hover:border-gray-400'
											}
										`}
									>
										<RadioGroupItem
											value={optionValue}
											id={`${field.field_key}-${optionValue}`}
											className='sr-only'
										/>
										<div className={`flex-shrink-0 w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center transition-all duration-200
											${isSelected ? 'border-gray-900 bg-gray-900' : 'border-gray-300 bg-white'}
										`}>
											{isSelected && (
												<div className='w-1.5 h-1.5 rounded-full bg-white' />
											)}
										</div>
										<span className={`font-montserrat-regular text-sm ${isSelected ? 'text-black font-montserrat-medium' : 'text-gray-600'}`}>
											{optionLabel}
										</span>
									</label>
								);
							})}
						</RadioGroup>
					);
				}
				return fieldWrapper(
					<Input
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(e) => handleInputChange(field, e.target.value)}
						placeholder={field.placeholder}
						required={field.is_required}
						className={inputClasses}
					/>
				);

			case 'multiselect':
			case 'checkbox':
				if (options.length > 0) {
					const selectedValues = Array.isArray(fieldValue) ? fieldValue : [];
					// Check if field has images enabled and options have imageUrls
					const cbHasImages = appearance?.enableImages && options.some(opt => opt.imageUrl);

					if (cbHasImages) {
						// Image card variant
						return fieldWrapper(
							<div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'>
								{options.map((option) => {
									const optionValue = option.value || option;
									const isSelected = selectedValues.includes(optionValue);
									return (
										<ImageOptionCard
											key={optionValue}
											option={option}
											fieldKey={field.field_key}
											isSelected={isSelected}
											variant='checkbox'
											onImageClick={setLightboxImage}
										>
											<Checkbox
												id={`${field.field_key}-${optionValue}`}
												name={field.field_key}
												checked={isSelected}
												onCheckedChange={(isChecked) => {
													let newValues;
													if (isChecked) {
														newValues = [...selectedValues, optionValue];
													} else {
														newValues = selectedValues.filter((v) => v !== optionValue);
													}
													handleInputChange(field, newValues);
												}}
												className='sr-only'
											/>
										</ImageOptionCard>
									);
								})}
							</div>
						);
					}

					// Text-only card variant (no images)
					return fieldWrapper(
						<div className='grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-80 overflow-y-auto p-1'>
							{options.map((option) => {
								const optionValue = option.value || option;
								const optionLabel = option.label || option;
								const isSelected = selectedValues.includes(optionValue);
								return (
									<label
										key={optionValue}
										htmlFor={`${field.field_key}-${optionValue}`}
										className={`relative flex items-center p-4 rounded-lg border cursor-pointer transition-all duration-300 ease-in-out
											${isSelected
												? 'border-black bg-gray-50/50'
												: 'border-gray-200 bg-white hover:border-gray-400'
											}
										`}
									>
										<Checkbox
											id={`${field.field_key}-${optionValue}`}
											name={field.field_key}
											checked={isSelected}
											onCheckedChange={(isChecked) => {
												let newValues;
												if (isChecked) {
													newValues = [...selectedValues, optionValue];
												} else {
													newValues = selectedValues.filter((v) => v !== optionValue);
												}
												handleInputChange(field, newValues);
											}}
											className='sr-only'
										/>
										<div className={`flex-shrink-0 w-4 h-4 rounded-sm border-2 mr-3 flex items-center justify-center transition-all duration-200
											${isSelected ? 'border-gray-900 bg-gray-900' : 'border-gray-300 bg-white'}
										`}>
											{isSelected && (
												<FiCheck className='h-2.5 w-2.5 text-white' />
											)}
										</div>
										<span className={`font-montserrat-regular text-sm ${isSelected ? 'text-black font-montserrat-medium' : 'text-gray-600'}`}>
											{optionLabel}
										</span>
									</label>
								);
							})}
						</div>
					);
				}
				return fieldWrapper(
					<Input
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(e) => handleInputChange(field, e.target.value)}
						placeholder='Enter values (comma separated)'
						required={field.is_required}
						className={inputClasses}
					/>
				);

			case 'heading':
				// Display element - no input needed
				return (
					<div className='py-4 mb-2'>
						<h3 className='text-base font-montserrat-medium text-black uppercase tracking-[0.15em]'>
							{field.label}
						</h3>
						{field.description && (
							<p className='text-sm font-montserrat-regular text-gray-500 mt-2 leading-relaxed'>{field.description}</p>
						)}
						<div className='mt-3 h-px bg-gradient-to-r from-gray-200 via-gray-200 to-transparent'></div>
					</div>
				);

			case 'paragraph':
				// Display element - no input needed
				return (
					<div className='py-3'>
						<div className='text-sm font-montserrat-regular text-gray-600 leading-relaxed'>
							{field.description || field.label}
						</div>
					</div>
				);

			case 'divider':
				// Visual divider - no input needed
				return (
					<div className='py-6'>
						<div className='relative'>
							<div className='h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent'></div>
							{field.label && field.label !== 'Section Divider' && (
								<div className='absolute inset-0 flex items-center justify-center'>
									<span className='bg-white px-4 text-xs font-montserrat-medium text-gray-400 uppercase tracking-wider'>
										{field.label}
									</span>
								</div>
							)}
						</div>
					</div>
				);

			case 'file':
			case 'image':
				return (
					<FileUploadField
						field={field}
						fieldValue={fieldValue}
						fieldError={fieldError}
						handleInputChange={handleInputChange}
						setErrors={setErrors}
						token={token}
						form={form}
						submitting={submitting}
						fieldWrapper={fieldWrapper}
					/>
				);

			case 'address':
				return fieldWrapper(
					<MapboxPlacesAutocomplete
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(address) => {
							handleInputChange(field, {
								street: address.street || '',
								city: address.city || '',
								state: address.state || '',
								zip: address.zip || '',
								coordinates: address.coordinates || null,
								formatted_address: address.formatted_address || '',
							});
						}}
						placeholder={field.placeholder || 'Enter an address'}
						required={field.is_required}
						error={fieldError}
						className={widthClass}
					/>
				);

			default:
				return fieldWrapper(
					<Input
						id={field.field_key}
						name={field.field_key}
						value={fieldValue}
						onChange={(e) => handleInputChange(field, e.target.value)}
						placeholder={field.placeholder}
						required={field.is_required}
						className={inputClasses}
					/>
				);
		}
	};

	// Validate form ID is available (must be after all hooks)
	if (!formId) {
		return (
			<div className='min-h-screen bg-gray-50 flex items-center justify-center p-4'>
				<div className='max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center'>
					<FiXCircle className='mx-auto h-12 w-12 text-red-500' />
					<h2 className='mt-4 text-2xl font-glacial text-gray-900'>
						Form Not Found
					</h2>
					<p className='mt-2 text-sm font-montserrat-regular text-gray-600'>
						The requested form could not be found or you don't have access to it.
					</p>
					<button
						onClick={handleCancel}
						className='mt-6 bg-clientBlack text-white px-6 py-3 font-montserrat-medium uppercase text-sm rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'>
						Return to Forms
					</button>
				</div>
			</div>
		);
	}

	if (loading) {
		return (
			<div className='flex justify-center items-center h-64'>
				<div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900'></div>
			</div>
		);
	}

	if (error || !form) {
		return (
			<div className='p-4'>
				<div className='p-6 border border-red-200 bg-red-50 rounded-lg'>
					<h2 className='text-xl font-semibold mb-2 text-red-800'>
						Error Loading Form
					</h2>
					<p className='text-red-600'>{error || 'Failed to load form'}</p>
				</div>
			</div>
		);
	}

	const headerImage = form?.headerImage || form?.header_image || '';

	return (
		<>
		<div className='min-h-screen bg-[#F7F7F5]'>
			<div className='max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-12 sm:py-16'>
				<div className='space-y-10'>
					{/* Back Button */}
					<div>
						<button
							onClick={handleCancel}
							className='inline-flex items-center px-3 py-2 text-sm font-montserrat-medium text-gray-500 hover:text-gray-900 rounded-lg hover:bg-white hover:shadow-sm transition-all duration-200'
							title='Go back'>
							<FiArrowLeft className='mr-2 h-4 w-4' />
							{backButtonText}
						</button>
					</div>

					{/* Form Header with elegant styling */}
					<div className='bg-white rounded-none sm:rounded-lg overflow-hidden'>
					{/* Banner Image — data-driven from form.headerImage or form.header_image */}
					{headerImage && (
						<div className='w-full h-40 sm:h-56 lg:h-72 overflow-hidden relative'>
							<img
								src={headerImage}
								alt=''
								className='w-full h-full object-cover brightness-[0.92]'
								loading='lazy'
							/>
							{/* Cinematic gradient fade into content */}
							<div className='absolute inset-0 bg-gradient-to-t from-white via-white/30 to-transparent' />
							{/* Subtle brand luminescence line */}
							<div className='absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#CC1DB9]/40 to-transparent' />
						</div>
					)}
					<div className={`px-6 sm:px-8 pb-8 relative z-10 ${headerImage ? 'pt-0 -mt-10 sm:-mt-12' : 'pt-8'}`}>
						<h1 className='text-4xl sm:text-5xl font-glacial font-bold text-black tracking-tight leading-tight'>
							{isEditing ? 'Edit ' : ''}{form?.name}
						</h1>
						{form?.description && (
							<p className='text-sm sm:text-base font-montserrat-regular text-gray-500 mt-4 leading-relaxed max-w-xl'>
								{form.description}
							</p>
						)}
						
						{/* Overall Progress Bar */}
						{form?.sections && form.sections.length > 1 && (() => {
							const progress = calculateOverallProgress();
							const { percentage, hasRequiredFields, hasFilledFields, isCompleted } = progress;

							// Determine what to show for overall form progress
							if (hasRequiredFields) {
								// Has required fields - show percentage progress bar
								return (
									<div className='mt-6 pt-6 border-t border-gray-100'>
										<div className='flex items-center justify-between mb-3'>
											<span className='text-xs font-montserrat-medium text-gray-400 uppercase tracking-[0.1em]'>
												Progress
											</span>
											<span className='text-xs font-montserrat-medium text-gray-400 tabular-nums'>
												{percentage}%
											</span>
										</div>
										<div className='w-full bg-gray-200/60 rounded-full h-1 overflow-hidden'>
											<div
												className='h-1 rounded-full transition-all duration-700 ease-out bg-gradient-to-r from-[#CC1DB9] to-[#CC1DB9]/80'
												style={{ width: `${percentage}%` }}
											></div>
										</div>
									</div>
								);
							} else if (hasFilledFields) {
								// No required fields but something is filled - show "Complete"
								return (
									<div className='mt-6 pt-6 border-t border-gray-100'>
										<div className='flex items-center justify-between'>
											<span className='text-sm font-montserrat-medium text-gray-700'>
												Progress
											</span>
											<span className='text-xs font-montserrat-medium text-gray-400 uppercase tracking-wider'>
												Complete
											</span>
										</div>
									</div>
								);
							} else {
								// No required fields and nothing filled - show "Optional"
								return (
									<div className='mt-6 pt-6 border-t border-gray-100'>
										<div className='flex items-center justify-between'>
											<span className='text-sm font-montserrat-medium text-gray-700'>
												Progress
											</span>
											<span className='text-sm font-montserrat-regular text-gray-400'>
												Optional form - fill at least one field
											</span>
										</div>
									</div>
								);
							}
						})()}
					</div>
					</div>

					{/* Section Navigation for Multi-Section Forms */}
					{form?.sections && form.sections.length > 2 && (
						<div className='bg-white rounded-xl shadow-sm border border-gray-100 p-6'>
							<h4 className='text-sm font-montserrat-medium text-gray-700 mb-4'>
								Jump to Section
							</h4>
							<div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'>
								{form.sections.map((section, index) => {
									const progress = sectionProgress[section.id] || buildProgressObject(0, false, false);
									const { percentage, hasRequiredFields, isCompleted } = progress;
									const hasProgress = hasRequiredFields ? percentage > 0 : progress.hasFilledFields;

									return (
										<button
											type='button'
											key={section.id}
											className={`flex items-center justify-between p-3 rounded-lg border transition-all duration-200 cursor-pointer text-left
												${isCompleted
													? 'bg-gray-900 border-gray-900 text-white hover:bg-gray-800'
													: hasProgress
														? 'bg-gray-50 border-gray-200 hover:bg-gray-100'
														: 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300'
												}
											`}
											onClick={() => {
												// Scroll to section
												const sectionElement = document.getElementById(`section-${section.id}`);
												if (sectionElement) {
													sectionElement.scrollIntoView({
														behavior: 'smooth',
														block: 'start'
													});
												}
											}}>
											<div className='flex items-center'>
												<div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-montserrat-medium mr-3
													${isCompleted
														? 'bg-white text-gray-900'
														: hasProgress
															? 'bg-gray-300 text-gray-700'
															: 'bg-gray-100 text-gray-500'
													}
												`}>
													{isCompleted ? (
														<FiCheck className='h-3 w-3' />
													) : (
														index + 1
													)}
												</div>
												<span className='text-sm font-montserrat-medium text-gray-700 truncate'>
													{section.title}
												</span>
											</div>
											<div className='flex items-center gap-2 ml-2'>
												{hasRequiredFields ? (
													<div className='w-10 bg-gray-200 rounded-full h-1.5'>
														<div
															className='h-1.5 rounded-full transition-all duration-300 bg-[#CC1DB9]'
															style={{ width: `${percentage}%` }}
														></div>
													</div>
												) : (
													<span className={`text-xs font-montserrat-medium ${isCompleted ? 'text-white' : 'text-gray-400'}`}>
														{isCompleted ? <FiCheck className='h-3 w-3' /> : 'Opt'}
													</span>
												)}
											</div>
										</button>
									);
								})}
							</div>
						</div>
					)}

					<form onSubmit={handleSubmit} className='space-y-10'>
						{form.sections && form.sections.length > 0 ? (
							<div className='space-y-10'>
								{form.sections.map((section, index) => {
									const progress = sectionProgress[section.id] || buildProgressObject(0, false, false);

									return (
										<div key={section.id} id={`section-${section.id}`} className='scroll-mt-20'>
											<div className='bg-white rounded-none sm:rounded-lg border-0 sm:border sm:border-gray-100/80 overflow-visible transition-all duration-300'>
												{/* Elegant Section Header */}
												<div className='border-b border-gray-100 px-6 sm:px-8 py-6'>
													<div className='flex items-center justify-between'>
														<div className='flex items-center space-x-3'>
															<h3 className='text-base font-montserrat-medium text-black uppercase tracking-[0.15em]'>
																{section.title}
															</h3>
															{progress.isCompleted && (
																<span className='inline-flex items-center text-xs font-montserrat-medium text-gray-400 tracking-wide uppercase'>
																	<FiCheck className='mr-1 h-3 w-3' />
																	Complete
																</span>
															)}
														</div>
														{renderProgressIndicator(progress)}
													</div>
													{section.description && (
														<p className='text-sm font-montserrat-regular text-gray-500 mt-2 leading-relaxed'>
															{section.description}
														</p>
													)}
												</div>

												{/* Section Content */}
												<div className='px-6 sm:px-8 py-8'>
													<div className='flex flex-col gap-6'>
														{getFieldsForSection(section.id).map((field) => (
															<div key={field.id}>{renderField(field)}</div>
														))}
													</div>
												</div>
											</div>
										</div>
									);
								})}

								{/* Virtual section for unassigned fields when sections exist */}
								{getUnassignedFields().length > 0 && (() => {
									const progress = calculateUnassignedFieldsProgress();

									return (
										<div>
											<div className='bg-white rounded-none sm:rounded-lg border-0 sm:border sm:border-gray-100/80 overflow-visible transition-all duration-300'>
												{/* Elegant Section Header */}
												<div className='border-b border-gray-100 px-6 sm:px-8 py-6'>
													<div className='flex items-center justify-between'>
														<div className='flex items-center space-x-3'>
															<h3 className='text-base font-montserrat-medium text-black uppercase tracking-[0.15em]'>
																Additional Information
															</h3>
															{progress.isCompleted && (
																<span className='inline-flex items-center text-xs font-montserrat-medium text-gray-400 tracking-wide uppercase'>
																	<FiCheck className='mr-1 h-3 w-3' />
																	Complete
																</span>
															)}
														</div>
														{renderProgressIndicator(progress)}
													</div>
												</div>
												{/* Section Content */}
												<div className='px-6 sm:px-8 py-8'>
													<div className='flex flex-col gap-6'>
														{getUnassignedFields().map((field) => (
															<div key={field.id}>{renderField(field)}</div>
														))}
													</div>
												</div>
											</div>
										</div>
									);
								})()}
							</div>
						) : (
							// Display unassigned fields directly when no sections exist
							<div className='bg-white rounded-none sm:rounded-lg border-0 sm:border sm:border-gray-100/80 overflow-visible transition-all duration-300'>
								{/* Elegant Section Header */}
								<div className='border-b border-gray-100 px-6 sm:px-8 py-6'>
									<h3 className='text-base font-montserrat-medium text-black uppercase tracking-[0.15em]'>
										Form Fields
									</h3>
								</div>
								{/* Section Content */}
								<div className='px-6 py-6'>
									<div className='flex flex-col gap-4'>
										{getUnassignedFields().map((field) => (
											<div key={field.id}>{renderField(field)}</div>
										))}
									</div>
								</div>
							</div>
						)}

						{/* Draft Status — minimal inline */}
						{(isDraftSaving || lastSaved) && (
							<div className='mt-4 flex items-center justify-end gap-2 text-xs font-montserrat-regular text-gray-400'>
								{isDraftSaving ? (
									<>
										<div className='animate-spin rounded-full h-3 w-3 border border-gray-300 border-t-gray-500'></div>
										<span>Saving...</span>
									</>
								) : lastSaved ? (
									<>
										<FiCheck className='h-3 w-3 text-gray-400' />
										<span>Draft saved {lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
									</>
								) : null}
							</div>
						)}

						{/* Form Completion Summary */}
						{/* Minimal completion status — single line */}
						{form?.sections && form.sections.length > 1 && (() => {
							const progress = calculateOverallProgress();
							const { percentage, hasRequiredFields, isCompleted } = progress;
							const completedSectionsCount = Object.values(sectionProgress).filter(p => p.isCompleted).length;

							return (
								<div className='mt-10 flex items-center justify-end'>
									<span className='text-xs font-montserrat-regular text-gray-400 uppercase tracking-wider'>
										{isCompleted
											? 'Ready to submit'
											: hasRequiredFields
												? `${completedSectionsCount} of ${form.sections.length} sections complete`
												: 'Optional form'
										}
									</span>
								</div>
							);
						})()}

						<div className='mt-12 pt-10 border-t border-gray-200 space-y-4'>
							{/* Clear Draft - demoted to text link */}
							{lastSaved && (
								<div className='flex justify-end'>
									<button
										type='button'
										onClick={() => {
											if (window.confirm('Are you sure you want to clear your saved draft? This action cannot be undone.')) {
												clearDraft();
												window.location.reload();
											}
										}}
										className='text-xs font-montserrat-medium text-gray-400 hover:text-red-500 underline underline-offset-2 transition-colors duration-300'>
										Clear Draft
									</button>
								</div>
							)}
							<div className='flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4'>
								<div className='text-xs font-montserrat-regular text-gray-500 uppercase tracking-wider'>
									<span className='text-[#CC1DB9]'>*</span> required
								</div>
								<div className='flex flex-col-reverse sm:flex-row gap-3 w-full sm:w-auto'>
									<button
										type='button'
										onClick={handleCancel}
										className='inline-flex items-center justify-center w-full sm:w-auto px-6 py-3 border border-gray-200 text-xs font-montserrat-medium rounded-none text-gray-500 bg-white uppercase tracking-[0.1em]
										hover:border-gray-400 hover:text-gray-700
										focus:outline-none focus:ring-1 focus:ring-black focus:ring-offset-2
										transition-colors duration-300'>
										Cancel
									</button>

									<button
										type='submit'
										disabled={submitting}
										className='inline-flex items-center justify-center w-full sm:w-auto px-10 py-3.5 border-0 text-xs font-montserrat-medium rounded-none text-white bg-gradient-to-r from-[#CC1DB9] to-[#CC1DB9]/90 uppercase tracking-[0.15em]
										shadow-lg shadow-[#CC1DB9]/20 hover:shadow-[0_8px_24px_-4px_rgba(204,29,185,0.35)] hover:-translate-y-px
										focus:outline-none focus:ring-1 focus:ring-[#CC1DB9] focus:ring-offset-2
										disabled:opacity-40 disabled:cursor-not-allowed
										active:translate-y-0 active:shadow-[0_2px_8px_-2px_rgba(204,29,185,0.25)] active:scale-[0.98] transition-all duration-150'>
										{submitting ? (
											<>
												<div className='animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2'></div>
												{isEditing ? 'Updating...' : 'Submitting...'}
											</>
										) : (
											isEditing ? 'Update Form' : 'Submit Form'
										)}
									</button>
								</div>
							</div>
						</div>
					</form>
				</div>
			</div>
		</div>

			{/* Validation Modal */}
			{validationModal.show && (
				<div className='fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4'>
					<div className='bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200'>
						{/* Header */}
						<div className='bg-red-50 px-6 py-5 border-b border-red-100'>
							<div className='flex items-center'>
								<div className='w-10 h-10 rounded-full bg-red-100 flex items-center justify-center mr-4'>
									<FiAlertCircle className='h-5 w-5 text-red-600' />
								</div>
								<h3 className='text-lg font-glacial font-semibold text-gray-900'>
									Please Complete Required Fields
								</h3>
							</div>
						</div>
						{/* Content */}
						<div className='px-6 py-5'>
							<p className='text-sm font-montserrat-regular text-gray-600 whitespace-pre-line leading-relaxed'>
								{validationModal.message}
							</p>
						</div>
						{/* Footer */}
						<div className='px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end'>
							<button
								onClick={() => setValidationModal({ show: false, message: '' })}
								className='inline-flex items-center px-5 py-2.5 text-sm font-montserrat-medium rounded-lg text-white bg-gray-900
								hover:bg-gray-800 shadow-sm
								focus:outline-none focus:ring-2 focus:ring-gray-900/30 focus:ring-offset-2
								transition-all duration-200'>
								Got it
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Image Lightbox */}
			<ImageLightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
		</>
	);
};

export default FormRenderer;
